/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react/prop-types */
import React, {
  useEffect,
  useMemo,
  FC,
  ReactElement,
} from 'react';
import {
  useLocation,
  matchPath,
} from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import useAuth from 'src/hooks/useAuth';
import {
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  ListSubheader,
  Typography,
  makeStyles,
  ListProps,
} from '@material-ui/core';
import NavItem from './NavItem';
import sections from './sections';
import type { Section, Item, NavBarProps } from './types';

function renderSections(navSections: Section[]): ReactElement[] {
  return navSections.map((section) => {
    const {
      subheader,
      key,
      items,
    } = section;

    const listProps: ListProps = {
      key,
    };

    if (subheader) {
      listProps.subheader = (
        <ListSubheader
          disableGutters
          disableSticky
        >
          {subheader}
        </ListSubheader>
      );
    }

    return (
      <List {...listProps}>
        {renderNavItems({
          items,
          pathname: window.location.pathname,
        })}
      </List>
    );
  });
}

function renderNavItems({
  items,
  pathname,
  depth = 0,
}: {
  items: Item[];
  pathname: string;
  depth?: number;
}): ReactElement {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({
          acc, item, pathname, depth,
        }),
        [],
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth,
}: {
  acc: any[];
  pathname: string;
  item: Item;
  depth: number;
}): ReactElement[] {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>,
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />,
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256,
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)',
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64,
  },
}));

const NavBar: FC<NavBarProps> = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sectionsList = useMemo(
    () => renderSections(sections),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections],
  );

  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Box
          p={3}
          textAlign="center"
        >
          <Typography
            variant="h5"
            color="textPrimary"
            gutterBottom
          >
            {user.name}
          </Typography>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            gutterBottom
          >
            {user.role}
          </Typography>
        </Box>
        <Divider />
        <Box p={2}>
          {sectionsList}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
